import React from 'react';
import { Box, Chip } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { PatientProfileInfoAccordion } from '../PatientProfileInfoAccordion';
import { ProviderCard } from './ProviderCard.component';
import { useQuery } from '@tanstack/react-query';
import { Asset, getProvidersUpliftLicensesTypes, Match, ProviderOverview, PublicUser, PublicUserProfile } from '@uplift/api';
import { Button } from '../../../../../Button';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { useToggleObject } from '../../../../../hooks';


const useStyles = makeStyles()(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    gap: theme.spacing(1),
  },
  listItem: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    gap: theme.spacing(1),
  },
}));


export type CareTeamAccordionProps = {
  currentProviderId: string;
  assets: Record<string, Asset>;
  activeMatches?: {
    match: Match;
    provider: PublicUser;
    providerProfile: PublicUserProfile;
    providerOverview: ProviderOverview;
  }[];
  archivedMatches?: {
    match: Match;
    provider: PublicUser;
    providerProfile: PublicUserProfile;
    providerOverview: ProviderOverview;
  }[];
  additionalContent?: React.ReactNode;
};


export const CareTeamAccordion: React.VFC<CareTeamAccordionProps> = ({ currentProviderId, assets, activeMatches = [], archivedMatches = [], additionalContent }) => {
  const { classes } = useStyles();
  const { value: archivedMatchesShown, toggle: toggleArchivedMatches } = useToggleObject(false);

  const activeMatchesList = React.useMemo(() => {
    const currentMatch = activeMatches?.find(m => m.provider.id === currentProviderId) ?? null;
    const restMatches = activeMatches?.filter(m => m !== currentMatch) ?? [];

    restMatches.sort((a, b) => a.provider.lastName.localeCompare(b.provider.lastName));

    if (currentMatch) {
      return [ currentMatch, ...restMatches ];
    }

    return restMatches;
  }, [ activeMatches, currentProviderId ]);

  const providerIds = React.useMemo(() => [ ...activeMatches, ...archivedMatches ]?.map(m => m.provider.id) ?? [], [ activeMatches, archivedMatches ]);
  const { data: providerLicensesTypes } = useQuery([ 'provider-licenses-types', providerIds ], async () => {
    const response = await getProvidersUpliftLicensesTypes(providerIds);

    return response.data;
  }, { enabled: !!providerIds?.length });
  const expandable = !!(activeMatchesList?.length || archivedMatches?.length);

  return (
    <PatientProfileInfoAccordion
      title="Care Team"
      chip={<Chip size="small" label={activeMatchesList?.length ?? 0} />}
      expandable={expandable}
    >
      <Box className={classes.root}>
        {activeMatchesList?.length ? activeMatchesList.map(({ match, provider, providerOverview, providerProfile }) => (
          <ProviderCard
            key={match.id}
            user={provider}
            overview={providerOverview}
            avatarUrl={providerProfile.avatarAssetId && assets?.[providerProfile.avatarAssetId]?.url}
            providerLicensesTypes={providerLicensesTypes?.[provider.id] ?? []}
            expandable={currentProviderId !== provider.id}
          />
        )) : null}
        {archivedMatches?.length ? (
          <>
            <Box ml={-1}>
              <Button
                size="small"
                style={{ textTransform: 'none' }}
                fullWidth={false}
                endIcon={archivedMatchesShown ? <ExpandLess /> : <ExpandMore />}
                onClick={toggleArchivedMatches}
              >
                {archivedMatchesShown ? 'Hide' : 'Show'} previous providers
              </Button>
            </Box>
            {archivedMatchesShown && archivedMatches.map(({ match, provider, providerOverview, providerProfile }) => (
              <ProviderCard
                key={match.id}
                user={provider}
                overview={providerOverview}
                avatarUrl={providerProfile.avatarAssetId && assets?.[providerProfile.avatarAssetId]?.url}
                providerLicensesTypes={providerLicensesTypes?.[provider.id] ?? []}
                expandable={currentProviderId !== provider.id}
              />
            ))}
          </>
        ) : null}
        {additionalContent}
      </Box>
    </PatientProfileInfoAccordion>
  );
};
